import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Link from "next/link"

import { Alert, AlertDescription, AlertTitle } from "@coval/ui"
import { AlertTriangle, Wrench } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import type {
  GetSystemStatusResponse,
  SystemStatusComponent,
} from "@/types_api/backend/transport/get_system_status_response"
import { buildScopedStorageKey } from "@/utils/scopedStorageKey"

type OverallStatus = GetSystemStatusResponse["overall_status"]

interface IncidentBannerSpec {
  tone: "info" | "warning" | "danger"
  title: string
  body: string
  /**
   * Identifies the notice for dismissal: the active incident set plus the
   * overall status while one is declared, otherwise the overall status alone,
   * so a status change or a new incident re-raises a dismissed banner. A
   * status-scoped dismissal is dropped once the notice changes or clears — or
   * the banner unmounts — so a later episode of the same status re-raises it
   * too.
   */
  dismissKey: string
}

const STATUS_NOTICES: Partial<
  Record<OverallStatus, Omit<IncidentBannerSpec, "body" | "dismissKey">>
> = {
  degraded_performance: {
    tone: "warning",
    title: "We're experiencing degraded performance",
  },
  partial_outage: {
    tone: "danger",
    title: "We're experiencing a partial service outage",
  },
  full_outage: {
    tone: "danger",
    title: "We're experiencing a service outage",
  },
  under_maintenance: {
    tone: "info",
    title: "Scheduled maintenance in progress",
  },
}

const STATUS_BODIES: Partial<Record<OverallStatus, string>> = {
  partial_outage: "Some Coval services are currently unavailable.",
  full_outage: "Coval services are currently unavailable.",
  under_maintenance: "Some Coval services may be briefly unavailable.",
}

const ACTIVE_INCIDENT_NOTICE: Omit<IncidentBannerSpec, "dismissKey"> = {
  tone: "warning",
  title: "We're tracking an active service incident",
  body: "Some Coval services may be affected.",
}

function joinNames(names: string[]): string {
  if (names.length === 1) {
    return names[0]
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`
  }
  return `${names.slice(0, -1).join(", ")}, and ${names.at(-1)}`
}

function degradedBody(components: SystemStatusComponent[]): string {
  // Only genuinely degraded components are named as running slow; an
  // unavailable or maintenance component under a degraded rollup must not be
  // described that way.
  const affected = components
    .filter((component) => component.status === "degraded_performance")
    .map((component) => component.name)
  if (affected.length === 0) {
    return "Some Coval services may run slower than usual."
  }
  return `${joinNames(affected)} may run slower than usual.`
}

function incidentBannerSpec(
  systemStatus: GetSystemStatusResponse,
): IncidentBannerSpec | null {
  // Fail closed: a stale or unavailable status source must not raise a false
  // alarm, even when its cached payload still names an incident.
  if (!systemStatus.source_available || systemStatus.is_stale) {
    return null
  }

  const activeIncidentIds = JSON.stringify(
    systemStatus.active_incidents.map((incident) => incident.id).sort(),
  )
  const dismissKey =
    systemStatus.active_incidents.length > 0
      ? `incidents:${activeIncidentIds}:${systemStatus.overall_status}`
      : `status:${systemStatus.overall_status}`

  // `operational` and `unknown` are deliberately unmapped. `unknown` is a
  // not-known rollup — the backend emits it even with a current source, for
  // an empty component set or an incomplete projection — and the banner
  // only speaks when the source says something concrete: a non-operational
  // status below, or an active incident after the fall-through. The
  // settings panel carries the "status unavailable" detail instead.
  const statusNotice = STATUS_NOTICES[systemStatus.overall_status]
  if (statusNotice !== undefined) {
    return {
      ...statusNotice,
      body:
        STATUS_BODIES[systemStatus.overall_status] ??
        degradedBody(systemStatus.components),
      dismissKey,
    }
  }

  if (systemStatus.active_incidents.length > 0) {
    return { ...ACTIVE_INCIDENT_NOTICE, dismissKey }
  }

  return null
}

const dismissStorageKey = (orgId: string, dismissKey: string) =>
  buildScopedStorageKey(
    `coval:system-status-banner-dismissed:${dismissKey}`,
    "organization",
    { organizationId: orgId },
  )

// `status:*` dismiss keys come from the closed set of statuses STATUS_NOTICES
// maps, so an observed all-clear can retire every stored status-only
// dismissal for the organization at once — including one written before a
// reload, which a fresh mount's transition cleanup cannot reach because it
// starts with no previous episode to compare against. Without this, that
// flag would hide a later episode of the same status for the whole session.
// Incident-scoped keys encode their own episode and are left alone.
const clearStoredStatusDismissals = (orgId: string) => {
  for (const status of Object.keys(STATUS_NOTICES)) {
    const storageKey = dismissStorageKey(orgId, `status:${status}`)
    if (storageKey === undefined) {
      continue
    }
    try {
      sessionStorage.removeItem(storageKey)
    } catch {
      // ignore storage errors
    }
  }
}

interface IncidentStatusBannerProps {
  className?: string
  systemStatus: GetSystemStatusResponse | null
  organizationId: string | null
  statusHref: string
}

export function IncidentStatusBanner({
  className,
  systemStatus,
  organizationId,
  statusHref,
}: IncidentStatusBannerProps) {
  const { trackEvent } = useAnalytics()
  const spec = useMemo(
    () => (systemStatus === null ? null : incidentBannerSpec(systemStatus)),
    [systemStatus],
  )
  const dismissKey = spec === null ? null : spec.dismissKey
  // A missing or non-current payload — a failed background refetch, an
  // organization-scope gap on the route, or a stale or unavailable status
  // source — observes nothing; the episode bookkeeping must hold through it
  // rather than reading it as the notice clearing.
  const isCurrentStatusPayload =
    systemStatus !== null &&
    systemStatus.source_available &&
    !systemStatus.is_stale
  // Dismissal is scoped to the notice identity and the organization's
  // storage: a stored value only counts for the key it was read for, so an
  // already-dismissed notice neither flashes nor tracks before hydration —
  // nor when polling swaps in a new notice — until session storage has been
  // read for the current key. The storage scope is part of that identity,
  // so one organization's in-memory dismissal cannot serve another's render
  // while a switch is settling.
  const [dismissedForKey, setDismissedForKey] = useState<{
    key: string
    storageKey: string | undefined
    value: boolean
  } | null>(null)
  const storageKey =
    organizationId === null || dismissKey === null
      ? undefined
      : dismissStorageKey(organizationId, dismissKey)
  const dismissed =
    dismissedForKey !== null &&
    dismissedForKey.key === dismissKey &&
    dismissedForKey.storageKey === storageKey
      ? dismissedForKey.value
      : null

  const overallStatus = systemStatus?.overall_status
  const activeIncidentCount = systemStatus?.active_incidents.length ?? 0

  // `trackEvent` is rebuilt as the PostHog client resolves and the Clerk
  // user/organization fields hydrate; keeping the latest one in a ref stops
  // those identity changes from re-running the viewed effect below, which
  // would count the same episode's view more than once.
  const trackEventRef = useRef(trackEvent)
  useEffect(() => {
    trackEventRef.current = trackEvent
  })

  // A status-scoped dismissal only covers the episode it dismissed: once the
  // notice identity changes or clears — or the banner unmounts, as it does on
  // the system-status page — drop the stored flag so a later episode of the
  // same status re-raises the banner. Incident-scoped keys already encode the
  // episode. An organization switch is not an episode change: dropping the
  // previous organization's stored dismissal on one would re-raise a banner
  // the user already dismissed once they switch back, so the bookkeeping
  // below ends an episode only on a notice change — never a storage-scope
  // change. A missing or non-current payload observes nothing, so it holds
  // the bookkeeping instead of ending the episode: a transient refetch
  // error, an organization-scope gap, or a stale status snapshot must not
  // drop a stored dismissal. A page reload does not run this cleanup, so
  // a reload within the same episode stays dismissed.
  const episodeRef = useRef<{
    dismissKey: string | null
    organizationId: string | null
    storageKey: string | undefined
  }>({ dismissKey: null, organizationId: null, storageKey: undefined })

  useEffect(() => {
    if (!isCurrentStatusPayload) {
      return
    }
    const previous = episodeRef.current
    const storageKey =
      organizationId === null || dismissKey === null
        ? undefined
        : dismissStorageKey(organizationId, dismissKey)
    if (
      previous.dismissKey !== null &&
      previous.dismissKey.startsWith("status:") &&
      previous.dismissKey !== dismissKey &&
      previous.organizationId === organizationId &&
      previous.storageKey !== undefined
    ) {
      try {
        sessionStorage.removeItem(previous.storageKey)
      } catch {
        // ignore storage errors
      }
    }
    episodeRef.current = { dismissKey, organizationId, storageKey }

    // An observed all-clear ends the stored status-only episodes the
    // transition cleanup above cannot reach on a fresh mount, which has no
    // previous episode to compare against — a dismissal stranded by a
    // reload must not outlive the healthy status that followed it. A null
    // dismissKey here means no active incidents either, so this is the full
    // all-clear.
    if (
      dismissKey === null &&
      overallStatus === "operational" &&
      organizationId !== null
    ) {
      clearStoredStatusDismissals(organizationId)
    }

    if (organizationId === null || dismissKey === null) {
      return
    }
    if (storageKey === undefined) {
      setDismissedForKey({ key: dismissKey, storageKey, value: false })
      return
    }
    try {
      setDismissedForKey({
        key: dismissKey,
        storageKey,
        value: sessionStorage.getItem(storageKey) === "true",
      })
    } catch {
      setDismissedForKey({ key: dismissKey, storageKey, value: false })
    }
  }, [dismissKey, organizationId, isCurrentStatusPayload, overallStatus])

  // Unmounting ends the episode too. This lives in its own effect so an
  // organization switch, which re-runs the effect above while the banner
  // stays mounted, cannot fire this cleanup and drop the previous
  // organization's dismissal; genuine unmounts — the system-status page,
  // sign-out — still end the episode.
  useEffect(() => {
    return () => {
      const { dismissKey, storageKey } = episodeRef.current
      if (
        dismissKey === null ||
        !dismissKey.startsWith("status:") ||
        storageKey === undefined
      ) {
        return
      }
      try {
        sessionStorage.removeItem(storageKey)
      } catch {
        // ignore storage errors
      }
    }
  }, [])

  useEffect(() => {
    if (dismissKey === null || dismissed !== false) {
      return
    }
    // Analytics is secondary and must never break the banner. Keyed on the
    // notice identity, not the spec object: each poll mints a fresh spec for
    // the same episode, and the event must count views, not polls.
    try {
      trackEventRef.current(TRACKING_EVENTS.SYSTEM_STATUS.BANNER_VIEWED, {
        overall_status: overallStatus,
        active_incident_count: activeIncidentCount,
      })
    } catch {
      // ignore analytics errors
    }
  }, [dismissKey, dismissed, overallStatus, activeIncidentCount])

  const handleDismiss = useCallback(() => {
    if (organizationId === null || spec === null) {
      return
    }
    const storageKey = dismissStorageKey(organizationId, spec.dismissKey)
    try {
      if (storageKey !== undefined) {
        sessionStorage.setItem(storageKey, "true")
      }
    } catch {
      // ignore storage errors
    }
    setDismissedForKey({
      key: spec.dismissKey,
      storageKey,
      value: true,
    })
    try {
      trackEvent(TRACKING_EVENTS.SYSTEM_STATUS.BANNER_DISMISSED, {
        overall_status: overallStatus,
        active_incident_count: activeIncidentCount,
      })
    } catch {
      // ignore analytics errors
    }
  }, [organizationId, spec, trackEvent, overallStatus, activeIncidentCount])

  const handleStatusLinkClick = useCallback(() => {
    try {
      trackEvent(TRACKING_EVENTS.SYSTEM_STATUS.BANNER_LINK_CLICKED, {
        overall_status: overallStatus,
      })
    } catch {
      // ignore analytics errors
    }
  }, [trackEvent, overallStatus])

  if (spec === null || dismissed !== false) {
    return null
  }

  return (
    <Alert
      variant={spec.tone}
      icon={spec.tone === "info" ? <Wrench /> : <AlertTriangle />}
      onDismiss={handleDismiss}
      className={className}
    >
      <AlertTitle>{spec.title}</AlertTitle>
      <AlertDescription>
        {spec.body}{" "}
        <Link
          href={statusHref}
          onClick={handleStatusLinkClick}
          className="font-medium underline underline-offset-2"
        >
          View system status
        </Link>
      </AlertDescription>
    </Alert>
  )
}
